<template>
    <transition name="modal">
        <div class="modal-mask"
             :id="id && `modal-${id}`"
             v-if="open"
             @click.self="clickOutsideClose && !loading && cancel()"
             :class="{ 'v2': isV2, 'v1': isV1 }">
            <div class="modal-container">
                <SystemMessage type="error"
                               :v1="isV1"
                               @acknowledged="$emit('errorMessageAcknowledged')"
                               :message="errorMessage"></SystemMessage>
                <div class="close-button-container" :class="{'with-separator': showHeaderSeparator}">
                    <slot v-if="showCloseButton" name="modal-close-button">
                        <span class="close-button" @click="!loading && cancel()">
                            <IconBase :iconName="isV1 ? 'closeIcon' : 'closeV2Icon'"
                                      class="close-icon"
                                      :iconColor="closeIconColor"
                                      :width="closeIconSize"
                                      :height="closeIconSize"></IconBase>
                        </span>
                    </slot>
                </div>

                <div class="modal-header"
                     :class="{ 'no-icon': !icon, 'header-with-separator': showHeaderSeparator }">
                    <slot name="modal-header">
                        <span v-if="icon"
                              class="icon-container"
                              :class="type">
                            <IconBase class="icon"
                                      verticalAlign="middle"
                                      :key="icon.name"
                                      :iconName="icon.name"
                                      :iconColor="icon.color"
                                      :width="icon.width"
                                      :height="icon.height"></IconBase>
                        </span>
                        <span class="title"
                              :class="{ 'headlineSmall': isV2,
                                        'font-3': isV1 && !showHeaderSeparator,
                                        'font-2': isV1 && showHeaderSeparator}">{{ title }}</span>
                    </slot>
                </div>

                <div class="modal-separator modal-separator-header" v-if="showHeaderSeparator"></div>

                <div class="modal-body" :class="{ 'body': isV2, 'no-header-separator': !showHeaderSeparator }">
                    <slot name="modal-body">
                        {{ body }}
                    </slot>
                </div>

                <div class="modal-separator modal-separator-footer" v-if="showFooterSeparator"></div>

                <div class="modal-footer" v-if="showFooter">
                    <slot name="modal-footer">
                        <slot name="modal-footer-content"></slot>
                        <button class="secondary"
                                :disabled="loading"
                                v-if="showCancelButton"
                                @click="cancel">
                            {{ cancelButtonText }}
                        </button>
                        <button class="primary subtitle"
                                :class="type"
                                :disabled="loading"
                                :style="{ width: okButtonWidth && `${okButtonWidth}px` }"
                                :id="okButtonText"
                                @click="!loading && $emit('ok')">
                            <IconBase v-if="loading && isV2"
                                      class="loader-icon"
                                      width="30"
                                      height="16"
                                      iconName="loaderIcon"/>
                            <InlineLoader v-if="loading && isV1" />
                            <span v-if="!loading || (loading && isV1)">{{ okButtonText }}</span>
                        </button>
                    </slot>
                </div>

            </div>
        </div>
    </transition>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import InlineLoader from 'components/loader/inlineLoader.vue';
import consts from 'services/metaData/consts';
import { iconMap as iconMapV2 } from 'services/overlayService/OverlayService';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';

const iconsMap = {
    V2: {
        ...iconMapV2
    },
    V1: {
        general: {
            name: 'notificationInfoIcon',
            color: consts.COLORS.LIGHT_700,
            width: 23,
            height: 23
        },
        error: {
            name: 'notificationErrorIcon',
            color: consts.COLORS.NEGATIVE_500,
            width: 18,
            height: 18
        },
        success: {
            name: 'notificationSuccessIcon',
            color: consts.COLORS.POSITIVE_500,
            width: 23,
            height: 23
        },
        warning: {
            name: 'warningIcon',
            color: consts.COLORS.NEUTRAL_500,
            width: 23,
            height: 23
        },
        delete: {
            name: 'deleteIcon',
            color: consts.COLORS.LIGHT_700,
            width: 23,
            height: 23
        }
    }
};

export default {
    name: 'Modal',
    components: { SystemMessage, InlineLoader, IconBase },
    props: {
        id: {
            type: Number
        },
        version: {
            type: Number,
            default: 2,
            validator: (value) => [1, 2].includes(value)
        },
        open: {
            type: Boolean,
            required: true
        },
        body: {
            type: String
        },
        title: {
            type: String
        },
        showCancelButton: {
            type: Boolean,
            default: true
        },
        showCloseButton: {
            type: Boolean,
            default: true
        },
        clickOutsideClose: {
            type: Boolean,
            default: true
        },
        okButtonText: {
            type: String,
            default: 'OK'
        },
        okButtonWidth: {
            type: Number
        },
        cancelButtonText: {
            type: String,
            default: 'Cancel'
        },
        loading: {
            type: Boolean
        },
        showHeaderSeparator: {
            type: Boolean,
            default: false
        },
        showFooterSeparator: {
            type: Boolean,
            default: true
        },
        showFooter: {
            type: Boolean,
            default: true
        },
        showIcon: {
            type: Boolean,
            default: true
        },
        type: {
            type: String,
            validator: (value) => ['general', 'error', 'success', 'warning', 'delete'].includes(value)
        },
        errorMessage: {
            type: String,
            default: ''
        }
    },
    computed: {
        icon() {
            return this.showIcon && iconsMap[`V${this.version}`][this.type];
        },
        isV1() {
            return this.version === 1;
        },
        isV2() {
            return this.version === 2;
        }
    },
    data() {
        return {
            closeIconColor: consts.COLORS.LIGHT_700,
            closeIconSize: 10
        };
    },
    methods: {
        cancel() {
            this.$emit('cancel');
        }
    }
};
</script>

<style lang="scss" scoped>
.modal-separator {
    height: 1px;
    width: 100%;

    &-footer {
        margin-top: auto;
    }

    &-header {
        margin-top: 14px;
    }
}

.modal-container {
    display: flex;
    flex-direction: column;
    min-height: 200px;
}

.modal-header {
    display: flex;
    align-items: center;

    &.no-icon {
        padding-top: 30px;
    }
}

.modal-body {
    margin: 10px 0 30px 0;
    line-height: 1.4;

    &.no-header-separator {
        margin-top: 19px;
    }
}

.icon-container {
    box-sizing: border-box;
    display: inline-flex;

    & + .title {
        margin-left: 12px;
    }

    &.error {
        margin-top: 1px;
    }
}

.icon.inlineBlock, .loader-icon.inlineBlock {
    display: inline-flex;
}

.v2 {
    @import "~styles/styleGuide.V2.scss";
    @import "~styles/buttons.V2.scss";

    .modal-header {
        padding: 18px 32px 0 32px;
    }

    .modal-container {
        width: 400px;
        border-radius: 16px;
    }

    .modal-separator {
        background: $light-300;
    }

    .title {
        font-weight: 600;
        color: $dark-600;
    }

    .modal-body {
        padding: 0 32px;
        color: $dark-600;
        line-height: 1.67;
    }

    .footer {
        margin-top: 12px;
        padding-bottom: 12px;
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
    }

    button {
        height: 24px;
        min-width: 60px;
        display: flex;
        justify-content: center;
        align-items: center;

        &.delete {
            background-color: $negative-500;
        }
    }

    button.primary {
        margin-left: $spacing-xs;
        margin-right: $spacing-xl;
    }

    .icon-container {
        width: 48px;
        height: 48px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;

        &.general {
            border: solid 2px $primary-200;
        }

        &.error, &.delete {
            border: solid 2px $negative-200;
        }

        &.success {
            border: solid 2px $secondary-200;
        }

        &.warning {
            border: solid 2px $neutral-200;
        }
    }

    .close-button {
        transform: translate(-19px, 19px);
    }

    .close-button-container {
        height: 10px;
    }
}

.v1 {
    @import "~styles/styleGuide";
    @import "~styles/buttons";

    .modal-header {
        padding: 18px 30px 0 30px;

        &.header-with-separator {
            padding-left: 20px;
            margin-bottom: 2px;
        }
    }

    .modal-container {
        width: 376px;
        border-radius: $borderRadius;
    }

    .modal-separator {
        background-color: $light-500;
    }

    .modal-footer {
        background-color: $light-100;
        padding-top: 20px;
        padding-bottom: 20px;
        border-bottom-left-radius: $borderRadius;
        border-bottom-right-radius: $borderRadius;
    }

    .title {
        font-weight: 600;
        color: $dark-500;
    }

    .modal-body {
        @include font(5);
        padding: 0 30px;
        color: $dark;
    }

    button.primary {
        margin-left: 15px;
        margin-right: 20px;
    }

    ::v-deep .loader-inline {
        position: absolute;
        left: 8px;
        height: 100%;
        top: 0;
        display: inline-flex;
        align-items: center;

        .loader-img-inline {
            height: 13px;

            div {
                border-color: $white;
            }
        }
    }

    .close-button {
        transform: translate(-20px, 16px);
    }

    .close-button-container {
        height: 16px;
        &.with-separator {
            height: 0;
        }
    }
}

.modal-mask {
    position: absolute;
    overflow: auto;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 20px 0;
    box-sizing: border-box;
    background-color: rgba(11, 22, 37, 0.6);
    display: flex;
    transition: opacity 0.1s ease;
}

.modal-container {
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
    box-sizing: border-box;
    margin: auto;
}

.modal-enter {
    opacity: 0;
}

.modal-leave-active {
    opacity: 0;
}

.modal-footer {
    padding-top: 12px;
    padding-bottom: 12px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
}

.modal-enter {
    opacity: 0;
}

.modal-leave-active {
    opacity: 0;
}

.close-button-container {
    display: flex;
    justify-content: flex-end;
}

.close-button {
    cursor: pointer;
}

.close-icon, .close-button {
    display: inline-flex;
}
</style>
